import React, { type DetailedHTMLProps, type HTMLAttributes, type JSX } from "react";
import { SupInput, SupFieldControl } from "@repo/core-ui";

import { FieldProps } from "../types";
import { useField } from "../hooks";

export interface TextFieldProps extends FieldProps {
  type?: "text" | "hidden" | "number" | "password" | "tel" | "url" | "email";
  suffixIcon?: JSX.Element;
  prefixIcon?: JSX.Element;
  clearable?: boolean;
  formatter?: (value: string, prevValue?: string) => string | undefined;
  inputProps?: Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    "id" | "name" | "value" | "defaultValue" | "disabled" | "placeholder" | "type" | "autoFocus" | "ref"
  > & { pattern?: string };
  readOnly?: boolean;
}

export function SupTextField({
  name,
  id = name,
  required,
  label,
  hint,
  hintAlign,
  prepend,
  className,
  controlClassName,
  fluid,
  disabled,
  autoFocus,
  color,
  defaultValue,
  placeholder,
  type,
  suffixIcon,
  prefixIcon,
  clearable,
  shouldUnregister,
  inputProps,
  icon,
  formatter,
  readOnly = false,
}: TextFieldProps) {
  const { ref, value = "", error, invalid, onChange, onBlur } = useField({ name, defaultValue, autoFocus, shouldUnregister });
  const fieldColor = invalid ? "error" : color;

  return (
    <SupFieldControl
      disabled={disabled}
      fluid={fluid}
      readOnly={readOnly}
      required={required}
      id={id}
      label={label}
      hint={invalid ? error : hint}
      hintAlign={hintAlign}
      prepend={prepend}
      className={controlClassName}
      color={fieldColor}
      icon={icon}
    >
      <SupInput
        ref={ref}
        readOnly={readOnly}
        id={id}
        type={type}
        suffixIcon={suffixIcon}
        prefixIcon={prefixIcon}
        name={name}
        className={className}
        fluid={fluid}
        color={fieldColor}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        clearable={clearable}
        formatter={formatter}
        onChange={onChange}
        onBlur={onBlur}
        inputProps={inputProps}
      />
    </SupFieldControl>
  );
}
